<script setup lang="ts">
import '@wangeditor/editor/dist/css/style.css'
import { nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import type { IDomEditor, IEditorConfig } from '@wangeditor/editor'

defineOptions({ name: 'HtmlRichEditor' })

const props = withDefaults(
  defineProps<{
    modelValue: string
    /** Editor area height (wangEditor inner root uses `height: 100%` — parent needs a definite height). */
    editorMinHeight?: string
    disabled?: boolean
  }>(),
  {
    modelValue: '',
    editorMinHeight: '440px',
    disabled: false,
  },
)

const emit = defineEmits<{ 'update:modelValue': [string] }>()

const localHtml = ref('')
const editorRef = shallowRef<IDomEditor | undefined>(undefined)
/** Skip echoing `update:modelValue` when syncing from parent (avoids fighting wang’s first paint). */
let muteEmit = false

const toolbarConfig = {}

const editorConfig: Partial<IEditorConfig> = {
  placeholder: '在此编辑 HTML 内容（可视化）…',
  scroll: true,
  readOnly: false,
  autoFocus: false,
}

watch(
  () => props.modelValue,
  (next) => {
    const n = next ?? ''
    if (n === localHtml.value)
      return
    muteEmit = true
    localHtml.value = n
    nextTick(() => {
      muteEmit = false
    })
  },
  { immediate: true },
)

watch(
  () => props.disabled,
  (ro) => {
    const e = editorRef.value
    if (!e)
      return
    if (ro)
      e.disable()
    else
      e.enable()
  },
)

watch(localHtml, (v) => {
  if (muteEmit)
    return
  emit('update:modelValue', v)
})

function handleCreated(editor: IDomEditor) {
  editorRef.value = editor
  if (props.disabled)
    editor.disable()
  /* 首次挂载时偶发 html 未灌入；与父级异步 content 对齐 */
  const want = props.modelValue ?? ''
  const got = (editor.getHtml() ?? '').trim()
  if (want.trim() && (got === '' || got === '<p><br></p>' || got === '<p><br/></p>'))
    editor.setHtml(want)
}

function handleDestroyed() {
  editorRef.value = undefined
}

onBeforeUnmount(() => {
  const e = editorRef.value
  if (e)
    e.destroy()
  editorRef.value = undefined
})
</script>

<template>
  <div class="html-rich-editor">
    <Toolbar
      class="html-rich-editor__toolbar"
      :editor="editorRef"
      :default-config="toolbarConfig"
      mode="default"
    />
    <!-- wangEditor 内部 `height: 100%`：此处必须给确定高度，否则正文区高度为 0（空白） -->
    <div class="html-rich-editor__shell" :style="{ height: editorMinHeight }">
      <Editor
        v-model="localHtml"
        class="html-rich-editor__editor"
        :default-config="editorConfig"
        mode="default"
        @onCreated="handleCreated"
        @onDestroyed="handleDestroyed"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.html-rich-editor {
  border: 1px solid var(--el-border-color);
  border-radius: 6px;
  overflow: hidden;
  background: var(--el-bg-color);
}

.html-rich-editor__toolbar {
  border-bottom: 1px solid var(--el-border-color);
}

.html-rich-editor__shell {
  box-sizing: border-box;
  min-height: 200px;
}

.html-rich-editor__editor {
  height: 100%;
  overflow-y: auto;
}
</style>
